export const SERVICE_MEDIA_MAX_ITEMS = 4;
export function normalizeServiceMediaItems(service) {
    if (!service)
        return [];
    const photoUrls = normalizeStringArray(service.photoUrls ?? service.photo_urls);
    const storageIds = normalizeValueArray(service.photo_storage_ids);
    if (photoUrls.length > 0 || storageIds.length > 0) {
        const itemCount = Math.max(photoUrls.length, storageIds.length);
        return dedupeServiceMediaItems(Array.from({ length: itemCount }, (_, index) => createMediaItem({
            source: "existing",
            photoUrl: photoUrls[index] ?? null,
            storageId: normalizeStorageId(storageIds[index]),
            fallbackKey: `existing-${index}-${photoUrls[index] ?? "missing-url"}`,
        })));
    }
    if (Array.isArray(service.photos)) {
        return dedupeServiceMediaItems(service.photos.map((photo, index) => createMediaItem({
            source: "existing",
            photoUrl: normalizeString(photo?.image_url ?? photo?.photoUrl ?? photo?.photo_url),
            storageId: normalizeStorageId(photo?.storage_id ?? photo?.storageId),
            fallbackKey: `legacy-${index}-${photo?.image_url ?? photo?.photoUrl ?? "missing-url"}`,
        })));
    }
    return [];
}
export function normalizeUploadedMediaItems(results) {
    const successful = Array.isArray(results?.successful) ? results.successful : [];
    return dedupeServiceMediaItems(successful.map((result, index) => {
        const storageId = normalizeStorageId(result.storageId ?? result.storage_id);
        const originalName = result.originalName ?? result.name ?? result.file?.name ?? null;
        const localPreviewUrl = createLocalPreviewUrl(result.file);
        const remoteUrl = normalizeUploadedDisplayUrl(result.downloadUrl ?? result.download_url);
        return createMediaItem({
            source: "uploaded",
            photoUrl: localPreviewUrl ?? normalizeString(result.preview) ?? remoteUrl,
            storageId,
            originalName,
            localPreviewUrl,
            ownsLocalPreviewUrl: Boolean(localPreviewUrl),
            fallbackKey: `uploaded-${result.id ?? index}-${originalName ?? "file"}`,
        });
    }));
}
export function mergeServiceMediaItems(currentItems, nextItems, maxItems = SERVICE_MEDIA_MAX_ITEMS) {
    const merged = [...currentItems];
    const addedItems = [];
    const skippedItems = [];
    const seenStorageIds = new Set(getServicePhotoStorageIds(currentItems));
    nextItems.forEach((item) => {
        if (item.storageId !== null && seenStorageIds.has(item.storageId)) {
            skippedItems.push(item);
            return;
        }
        if (merged.length >= maxItems) {
            skippedItems.push(item);
            return;
        }
        merged.push(item);
        addedItems.push(item);
        if (item.storageId !== null) {
            seenStorageIds.add(item.storageId);
        }
    });
    return {
        items: merged,
        addedItems,
        skippedItems,
    };
}
export function getServicePhotoStorageIds(items) {
    const seen = new Set();
    const ids = [];
    items.forEach((item) => {
        if (item.storageId === null || seen.has(item.storageId))
            return;
        seen.add(item.storageId);
        ids.push(item.storageId);
    });
    return ids;
}
export function buildServicePhotosChangedPayload(items, initialPhotoStorageIds = []) {
    const photoStorageIds = getServicePhotoStorageIds(items);
    const current = new Set(photoStorageIds);
    const initial = new Set(initialPhotoStorageIds);
    return {
        photo_storage_ids: photoStorageIds,
        photoStorageIds,
        addedIds: photoStorageIds.filter((id) => !initial.has(id)),
        removedIds: initialPhotoStorageIds.filter((id) => !current.has(id)),
        items: items.map((item) => ({ ...item })),
    };
}
export function revokeServiceMediaPreview(item) {
    if (!item?.ownsLocalPreviewUrl || !item.localPreviewUrl)
        return;
    URL.revokeObjectURL(item.localPreviewUrl);
    item.ownsLocalPreviewUrl = false;
    item.localPreviewUrl = null;
}
export function revokeServiceMediaPreviews(items) {
    items.forEach(revokeServiceMediaPreview);
}
function createMediaItem(input) {
    return {
        id: input.storageId !== null ? `storage-${input.storageId}` : sanitizeMediaKey(input.fallbackKey),
        source: input.source,
        photoUrl: input.photoUrl,
        storageId: input.storageId,
        originalName: input.originalName ?? null,
        localPreviewUrl: input.localPreviewUrl ?? null,
        ownsLocalPreviewUrl: input.ownsLocalPreviewUrl ?? false,
        expired: false,
    };
}
function dedupeServiceMediaItems(items) {
    const seenIds = new Set();
    const seenStorageIds = new Set();
    return items.filter((item) => {
        if (item.storageId !== null) {
            if (seenStorageIds.has(item.storageId))
                return false;
            seenStorageIds.add(item.storageId);
        }
        if (seenIds.has(item.id))
            return false;
        seenIds.add(item.id);
        return true;
    });
}
function normalizeStorageId(value) {
    if (value === null || value === undefined || value === "")
        return null;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
}
function normalizeString(value) {
    return typeof value === "string" && value.length > 0 ? value : null;
}
function normalizeUploadedDisplayUrl(value) {
    const url = normalizeString(value);
    if (!url)
        return null;
    if (url.includes("storage.googleapis.com") && !url.includes("?")) {
        return null;
    }
    return url;
}
function createLocalPreviewUrl(file) {
    if (!file?.type?.startsWith("image/"))
        return null;
    return URL.createObjectURL(file);
}
function normalizeStringArray(value) {
    return Array.isArray(value) ? value.filter((item) => typeof item === "string") : [];
}
function normalizeValueArray(value) {
    return Array.isArray(value) ? value : [];
}
function sanitizeMediaKey(value) {
    return value.replace(/[^a-zA-Z0-9_-]/g, "-");
}
